import { useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { Marker, NavigationControl, Map as ReactMapGL } from 'react-map-gl/mapbox';
import { ButtonUI, showNotification } from 'sonata-react-components';

import ImageEditMenu from '@/components/common/ImageEditMenu/ImageEditMenu';
import { getPoiImageUrl, getPoiLogoUrl, openGoogleMaps } from '@/helpers/common/poiAttribution.helper';
import { deletePoiImageOverride, setPoiImageOverride } from '@/services/footfall.service';

import type { Poi } from '@/types/footfall';

import './storeHeader.scss';

type State = {
  imgError: boolean;
  logoError: boolean;
  overrideImageUrl: string | null;
  isLoading?: boolean;
};

type Props = {
  poi: Poi;
  userId: string;
};

export default function StoreHeader({ poi, userId }: Props) {
  const { formatMessage } = useIntl();
  const buttonId = useId();
  const mapId = `storeHeaderMap_${poi.id}`;
  const [{ imgError, logoError, overrideImageUrl, isLoading }, setState] = useState<State>({
    imgError: false,
    isLoading: false,
    logoError: false,
    overrideImageUrl: poi.imageUrl ?? null
  });

  const imageUrl = overrideImageUrl === null ? getPoiImageUrl(poi.id, poi.tags?.level0?.[0]) : overrideImageUrl;
  const logoUrl = getPoiLogoUrl(poi.logo_s3);
  const showImage = !!imageUrl && !imgError;
  const showLogo = !!logoUrl && !logoError;
  const hasCoordinates = poi.lat != null && poi.lng != null;

  const onUploadImage = async (rawFile: File) => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      const result = await setPoiImageOverride(poi.id, userId, rawFile);
      setState(prev => ({ ...prev, imgError: false, isLoading: false, overrideImageUrl: result.imageUrl }));
      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'Image uploaded successfully',
          id: 'driveToStore.footfallReport.storeHeader.uploadImageSuccess'
        })
      );
    } catch (_error) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'An error occurred while uploading the image. Please try again.',
          id: 'driveToStore.footfallReport.storeHeader.uploadImageError'
        })
      );
      setState(prev => ({ ...prev, imgError: true, isLoading: false }));
    }
  };

  const onRemoveImageClick = async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      await deletePoiImageOverride(poi.id, userId);
      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'Image removed successfully',
          id: 'driveToStore.footfallReport.storeHeader.removeImageSuccess'
        })
      );
    } catch (_error) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'An error occurred while removing the image. Please try again.',
          id: 'driveToStore.footfallReport.storeHeader.removeImageError'
        })
      );
    }
    setState(prev => ({ ...prev, imgError: false, isLoading: false, overrideImageUrl: null }));
  };

  return (
    <div className="storeHeader">
      <div className="storeHeader__mediaRow">
        <div className="storeHeader__logo">
          {showLogo ? (
            // biome-ignore lint/a11y/noNoninteractiveElementInteractions: onError is a native img fallback handler
            <img
              alt={poi.name}
              className="storeHeader__logoImage"
              onError={() => setState(prev => ({ ...prev, logoError: true }))}
              src={logoUrl}
            />
          ) : (
            <div className="storeHeader__logoPlaceholder">
              <i className="fa-kit-duotone fa-poi-inventory storeHeader__logoPlaceholderIcon" />
            </div>
          )}
        </div>
        <div className="storeHeader__storeImage">
          {showImage ? (
            // biome-ignore lint/a11y/noNoninteractiveElementInteractions: onError is a native img fallback handler
            <img
              alt={poi.name}
              className="storeHeader__image"
              onError={() => setState(prev => ({ ...prev, imgError: true }))}
              src={imageUrl}
            />
          ) : (
            <div className="storeHeader__placeholder">
              <i className="fa-kit-duotone fa-poi-inventory storeHeader__placeholderIcon" />
            </div>
          )}
          <ImageEditMenu
            accept=".jpg,.jpeg,.png"
            buttonClassName="storeHeader__imageEditButton"
            inputName="poi-image-upload"
            isLoading={isLoading}
            onReset={overrideImageUrl ? onRemoveImageClick : undefined}
            onUpload={onUploadImage}
            texts={{
              change: formatMessage({
                defaultMessage: 'Change picture',
                id: 'driveToStore.footfallReport.storeHeader.changePicture'
              }),
              edit: formatMessage({
                defaultMessage: 'Edit image',
                id: 'driveToStore.footfallReport.storeHeader.editImage'
              }),
              fileTooLarge: formatMessage({
                defaultMessage: 'The image exceeds the maximum size of 2MB.',
                id: 'driveToStore.footfallReport.storeHeader.fileTooLarge'
              }),
              reset: formatMessage({
                defaultMessage: 'Reset to default',
                id: 'driveToStore.footfallReport.storeHeader.resetToDefault'
              })
            }}
          />
        </div>
      </div>
      {hasCoordinates && (
        <div className="storeHeader__map">
          <ReactMapGL
            attributionControl={false}
            id={mapId}
            initialViewState={{ latitude: poi.lat!, longitude: poi.lng!, zoom: 17 }}
            mapStyle="mapbox://styles/mapbox/streets-v12"
            mapboxAccessToken={taptap.config.mapbox.accessToken}
            maxZoom={18}
            minZoom={4}
            scrollZoom
          >
            <NavigationControl position="bottom-right" showCompass={false} visualizePitch={false} />
            <Marker latitude={poi.lat!} longitude={poi.lng!}>
              <i className="fa-solid fa-location-dot storeHeader__mapMarker" />
            </Marker>
          </ReactMapGL>
        </div>
      )}
      <div className="storeHeader__info">
        <div className="storeHeader__infoContainer">
          <h2 className="storeHeader__name" title={poi.name}>
            {poi.name}
          </h2>
          <p className="storeHeader__address" title={poi.address}>
            {poi.address}
          </p>
        </div>
        <ButtonUI
          className="storeHeader__buttonMap"
          icon="fa-regular fa-map"
          iconPosition="left"
          id={buttonId}
          onClick={() => openGoogleMaps(poi)}
          size="small"
          text={formatMessage({ defaultMessage: 'Go to map', id: 'driveToStore.footfallReport.storeHeader.goToMap' })}
          type="primary"
        />
      </div>
    </div>
  );
}
